import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Bug, Search } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useBugReports,
  type BugReportWithReporter,
  type BugSeverity,
  type BugStatus,
} from "@/hooks/useBugReports";
import { BugDetailSheet } from "@/components/bugs/BugDetailSheet";

const STATUS_OPTIONS: { value: BugStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "wont_fix", label: "Won't fix" },
];

const SEVERITY_OPTIONS: { value: BugSeverity | "all"; label: string }[] = [
  { value: "all", label: "All severities" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const SEVERITY_STYLES: Record<BugSeverity, string> = {
  low: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  medium: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  high: "bg-red-500/10 text-red-600 border-red-500/20",
};

const STATUS_STYLES: Record<BugStatus, string> = {
  open: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  in_progress: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  resolved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  wont_fix: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABELS: Record<BugStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  wont_fix: "Won't fix",
};

export function BugsTab() {
  const { data: reports = [], isLoading } = useBugReports();
  const [statusFilter, setStatusFilter] = useState<BugStatus | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<BugSeverity | "all">("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reports.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (severityFilter !== "all" && r.severity !== severityFilter) return false;
      if (!q) return true;
      const hay = [
        r.title,
        r.description ?? "",
        r.page_url ?? "",
        r.screenshot_text ?? "",
        r.reporter?.display_name ?? "",
        r.reporter?.email ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [reports, statusFilter, severityFilter, search]);

  const selected = useMemo(
    () => reports.find((r) => r.id === selectedId) ?? null,
    [reports, selectedId],
  );

  const counts = useMemo(() => {
    const open = reports.filter((r) => r.status === "open").length;
    const inProgress = reports.filter((r) => r.status === "in_progress").length;
    return { open, inProgress, total: reports.length };
  }, [reports]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Bug Reports
          </CardTitle>
          <CardDescription>
            User-submitted reports with auto-captured browser context. Click a row to triage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Total" value={counts.total} />
            <Stat label="Open" value={counts.open} />
            <Stat label="In progress" value={counts.inProgress} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, description, OCR text, reporter…"
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as BugStatus | "all")}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as BugSeverity | "all")}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEVERITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading reports…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {reports.length === 0 ? "No bug reports yet." : "No reports match these filters."}
            </p>
          ) : (
            <ScrollArea className="h-[480px] rounded-md border">
              <ul className="divide-y">
                {filtered.map((report) => (
                  <BugRow
                    key={report.id}
                    report={report}
                    onClick={() => setSelectedId(report.id)}
                  />
                ))}
              </ul>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <BugDetailSheet
        report={selected}
        open={!!selected}
        onOpenChange={(open) => !open && setSelectedId(null)}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  );
}

function BugRow({
  report,
  onClick,
}: {
  report: BugReportWithReporter;
  onClick: () => void;
}) {
  const reporter = report.reporter?.display_name || report.reporter?.email || "Unknown";
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium truncate">{report.title}</p>
            {report.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{report.description}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {reporter} · {format(new Date(report.created_at), "MMM d, yyyy h:mm a")}
              {report.page_url && (
                <>
                  {" · "}
                  <span className="font-mono">{shortenUrl(report.page_url)}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            <Badge variant="outline" className={SEVERITY_STYLES[report.severity]}>
              {report.severity}
            </Badge>
            <Badge variant="outline" className={STATUS_STYLES[report.status]}>
              {STATUS_LABELS[report.status]}
            </Badge>
          </div>
        </div>
      </button>
    </li>
  );
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + (u.search ? u.search : "");
  } catch {
    return url;
  }
}
